import { DialogActions, DialogContent, Stack } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { ContainedButton, TextButton } from '@shared/components';
import {
  useMutateCreatePriceSuggestionPrices,
  useMutateValidatePriceSuggestionPrices,
} from '@retail/price-suggestion/data-access';
import { useImportPriceSuggestionPricesStore } from '@retail/price-suggestion/stores';
import { useCallback, useEffect, useMemo } from 'react';
import { ImportedPriceSuggestionPrice } from '@retail/price-suggestion/types';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { UploadContainer } from '@retail/products/components';
import { LocalPriceSuggestionPricesUploadValidation } from '@retail/price-suggestion/components';
import { BodyLoader } from '@retail/components';
import { downloadTemplateCsv } from '@retail/products/utils';

interface Props {
  assortmentId: number;
  onClose: () => void;
}

const csvData = '\uFEFFMgItem number;supplier id;sales price;from date;to date';

export function UploadPriceSuggestionPrices({ assortmentId, onClose }: Props) {
  const t = useAppTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: postPriceSuggestionPrices } = useMutateCreatePriceSuggestionPrices({
    assortmentId,
  });
  const {
    mutateAsync: validatePriceSuggestionPrices,
    data: invalidRows,
    isPending,
    reset,
  } = useMutateValidatePriceSuggestionPrices({ assortmentId });

  const handleValidatePriceSuggestionPrices = useCallback(
    (mgItemPrices: ImportedPriceSuggestionPrice[]) => {
      validatePriceSuggestionPrices({ body: mgItemPrices }).catch((e) => console.error(e));
    },
    [validatePriceSuggestionPrices]
  );
  const { importedItemPrices, missingColumns, importedFile, importSpreadsheet, clearState } =
    useImportPriceSuggestionPricesStore((state) => state);

  const rowsWithSalesPrice = useMemo(
    () =>
      importedItemPrices?.filter((itemPrices) => !!itemPrices.price && !!Number(itemPrices.price)),
    [importedItemPrices]
  );

  useEffect(() => {
    if (rowsWithSalesPrice) {
      handleValidatePriceSuggestionPrices(rowsWithSalesPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsWithSalesPrice]);

  const validRows = useMemo(() => {
    const invalidItemNumbers = invalidRows?.invalidItemNumbers.map((item) => item.mgItemNumber);
    const invalidSupplierIds = invalidRows?.invalidItemNumbers.map((item) => item.mgSupplierId);

    return rowsWithSalesPrice?.filter(
      (item) =>
        item.mgItemNumber &&
        item.mgSupplierId &&
        !(
          invalidItemNumbers?.includes(item.mgItemNumber) &&
          invalidSupplierIds?.includes(item.mgSupplierId)
        )
    );
  }, [rowsWithSalesPrice, invalidRows?.invalidItemNumbers]);

  useEffect(() => {
    clearState();
    reset();
  }, [clearState, reset]);

  const handleSubmitPrices = useCallback(() => {
    postPriceSuggestionPrices({ body: validRows })
      .then(() => {
        toast.success(t('products.uploadItems.success'));
        onClose();
        queryClient.invalidateQueries({ queryKey: ['price-suggestion-prices'] });
      })
      .catch((e) => {
        console.error(e);
        toast.error(t('calculus.error.generic'));
      });
  }, [onClose, postPriceSuggestionPrices, queryClient, t, toast, validRows]);

  return (
    <>
      <DialogContent sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
        <Stack flexGrow={1} justifyContent="center">
          <UploadContainer
            importedFile={importedFile}
            isLarge
            onDownloadTemplate={() =>
              downloadTemplateCsv(csvData, 'localPriceSuggestionTemplate.csv')
            }
            importSpreadsheet={importSpreadsheet}
            resetImportedStates={clearState}
            renderValidation={
              isPending ? (
                <BodyLoader />
              ) : (
                <LocalPriceSuggestionPricesUploadValidation
                  validRows={validRows}
                  invalidRows={invalidRows?.invalidItemNumbers?.length}
                  totalRows={rowsWithSalesPrice?.length}
                  missingColumns={missingColumns}
                />
              )
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <TextButton onClick={onClose} size="small">
          {t('common.cancel')}
        </TextButton>
        <ContainedButton
          size="small"
          color="secondary"
          disabled={
            invalidRows?.invalidItemNumbers.length === rowsWithSalesPrice?.length || !invalidRows
          }
          onClick={handleSubmitPrices}
        >
          {t('calculus.actions.importPrices')}
        </ContainedButton>
      </DialogActions>
    </>
  );
}
